import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { type DcTenantCode, getDcEnvironmentFromCode, getTenantFromCode } from '@rma/generic/core/instance';
import { getRmaBaseUrl } from '@rma/generic/core/urls';
import { LazyInject } from '@rma/generic/util/lazy-inject';
import { HardNavigationService, PostHogService } from '@rma/generic/util/posthog';
import { CountryCode, Environment, PlatformService } from '@rma/public/generic/util/environment';
import { filter, firstValueFrom } from 'rxjs';
import { VisitorRegionApiService } from '../data-access-visitor-region/visitor-region-api.service';
import type {
  WrongRegionModalComponent,
  WrongRegionModalData,
  WrongRegionModalResult,
} from '../orc-wrong-region-modal/wrong-region-modal.component';

const DISMISS_STORAGE_KEY = 'rma.geo-redirect.dismissed';

const COUNTRY_TENANT_MAP: Record<CountryCode, DcTenantCode> = {
  AU: 'au',
  NZ: 'nz',
  US: 'us',
  CA: 'us',
};

@Injectable({
  providedIn: 'root',
})
export class WrongRegionModalService {
  private readonly visitorRegionApiService = inject(VisitorRegionApiService);
  private readonly lazyInject = inject(LazyInject);
  private readonly postHog = inject(PostHogService);
  private readonly environment = inject(Environment);
  private readonly platformService = inject(PlatformService);
  private readonly localStorage = inject<Storage>(WA_LOCAL_STORAGE);
  private readonly router = inject(Router);
  private readonly hardNavigation = inject(HardNavigationService);

  public async initialise(): Promise<void> {
    try {
      if (this.platformService.isPlatformServer) {
        return;
      }

      if (this.platformService.isPlatformBrowserBot) {
        return;
      }

      if (this.localStorage.getItem(DISMISS_STORAGE_KEY) != null) {
        return;
      }

      if (!this.router.navigated) {
        await firstValueFrom(
          this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
          ),
        );
      }

      if (this.isSuppressedByRoute()) {
        return;
      }

      const detectedCountry = await firstValueFrom(this.visitorRegionApiService.getCountry());

      if (detectedCountry == null) {
        return;
      }

      if (COUNTRY_TENANT_MAP[detectedCountry] === COUNTRY_TENANT_MAP[this.environment.countryCode]) {
        return;
      }

      await this.openModal(this.environment.countryCode, detectedCountry);
    } catch {
      // Fail-open: any unhandled exception in the orchestration path resolves to no-modal.
    }
  }

  private isSuppressedByRoute(): boolean {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      if (snapshot.data['suppressWrongRegionModal']) {
        return true;
      }
      snapshot = snapshot.firstChild;
    }
    return false;
  }

  private async openModal(currentRegion: CountryCode, detectedCountry: CountryCode): Promise<void> {
    const [modalService, { WrongRegionModalComponent }] = await Promise.all([
      // Injecting RmaModalService directly would put Material dialog in the bundle every page loads.
      this.lazyInject.get(() => import('@rma/public/generic/ui/modal').then((m) => m.RmaModalService)),
      import('../orc-wrong-region-modal/wrong-region-modal.component'),
    ]);
    const dialogRef = modalService.open<WrongRegionModalComponent, WrongRegionModalData, WrongRegionModalResult>(
      WrongRegionModalComponent,
      {
        data: { currentRegion, detectedCountry },
        panelClass: 'wrong-region-modal',
        ariaLabelledBy: 'rma-wrong-region-modal-heading',
      },
    );

    this.postHog.capture('Wrong Region Modal Shown', {
      current_region: currentRegion,
      detected_country: detectedCountry,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'accept') {
        this.handleAccept(currentRegion, detectedCountry);
      } else {
        this.handleDismiss(currentRegion, detectedCountry);
      }
    });
  }

  private handleAccept(currentRegion: CountryCode, detectedCountry: CountryCode): void {
    const targetDomain = this.getRedirectUrl(detectedCountry);

    this.postHog.capture('Wrong Region Modal Accepted', {
      current_region: currentRegion,
      detected_country: detectedCountry,
      target_domain: targetDomain,
    });

    this.hardNavigation.assign(targetDomain);
  }

  private handleDismiss(currentRegion: CountryCode, detectedCountry: CountryCode): void {
    this.localStorage.setItem(DISMISS_STORAGE_KEY, '1');

    this.postHog.capture('Wrong Region Modal Dismissed', {
      current_region: currentRegion,
      detected_country: detectedCountry,
    });
  }

  private getRedirectUrl(countryCode: CountryCode): string {
    return getRmaBaseUrl(getTenantFromCode(COUNTRY_TENANT_MAP[countryCode]), getDcEnvironmentFromCode(this.environment.type));
  }
}
